import React, { useCallback, useEffect, useState } from 'react';

import { 
  DisablableOption, 
  ModelBase, 
  UpsertableBase, 
  toDisablableOption
} from "@models";
import { DataType } from '@dndModels/Types';

import DandDAPI from '@dndUtilities/system_api';
import CommonUtilities from '@utilities/common_utilities';
import DandDCharacterUtilities from '@dndUtilities/char_utilities';

import SelectStringBox from '@components/input/selects/SelectStringBox';

interface Props {
  name: string;
  value?: string;
  values?: string[];
  data_type?: DataType;
  data_types?: DataType[];
  option_objects?: UpsertableBase[];
  priority_extra_options?: (string | DisablableOption)[];
  extra_options?: (string | DisablableOption)[];
  filter?: any;
  filters?: any;
  limit?: number;
  extra_filter?: (o: UpsertableBase) => boolean;
  color?: string;
  multiple?: boolean;
  disabled?: boolean;
  auto_sort?: boolean;
  onChange: Function;
  display_format?: string;
  use_field?: string;
  max_selected?: number;
  log_it?: boolean;
  show_details?: boolean;
  dominant_only?: boolean;
}

interface ViewData { 
  data_type?: DataType;
  data_types: DataType[];
  option_objects: UpsertableBase[];
  priority_extra_options: (string | DisablableOption)[];
  extra_options: (string | DisablableOption)[];
  filter?: any;
  filters?: any; 
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

const parseData = ({ data_type,
  data_types,
  option_objects,
  priority_extra_options,
  extra_options,
  filter,
  filters 
}: ViewData): ViewData => {
  return { 
    data_type, 
    data_types: [...data_types], 
    option_objects: option_objects.map(o => o.clone(false)),
    priority_extra_options: [...priority_extra_options],
    extra_options: [...extra_options],
    filter: filter ? {...filter} : undefined,
    filters: filters ? {...filters} : undefined
  };
}

const dataMatches = (data1: ViewData, data2: ViewData) => {
  return common_util.objectsMatchLight(data1, data2);
}

// const findMismatch = (data1: ViewData, data2: ViewData) => {
//   return common_util.findMismatchLight(data1, data2);
// }

export default function SelectObjectBoxSimple({ 
  name,
  value,
  values,
  data_type,
  data_types = [],
  priority_extra_options = [],
  option_objects = [],
  filter,
  filters,
  limit,
  extra_filter,
  color = "",
  multiple = false,
  disabled = false,
  auto_sort = false,
  extra_options = [],
  onChange,
  display_format,
  use_field,
  max_selected,
  log_it = false,
  show_details = false,
  dominant_only = false
}: Props) {
  const [options, setOptions] = useState<DisablableOption[]>([]); 
  const [my_extra_options, setExtraOptions] = useState<DisablableOption[]>([]);
  const [prev_data, setPrevData] = useState<ViewData>({ data_types: [], option_objects: [], priority_extra_options: [], extra_options: [] });

  const load = useCallback(async () => {
    const data = parseData({ data_type, data_types, option_objects, priority_extra_options, extra_options, filter, filters });
    // console.log(data);
    // console.log(prev_data);
    if (!dataMatches(data, prev_data)) {
      // console.log("loading");
      // console.log(findMismatch(data, prev_data));
      setPrevData(data);
      if (log_it) {
        console.log(option_objects);
        console.log(extra_options);
        console.log(data_type);
        console.log(data_types);
        console.log(filter);
        console.log(filters);
        debugger;
      }
      let use_objects = option_objects;
      if (use_objects.length === 0) {
        if (data_type) {
          const dt = data_type;
          use_objects = await api.getObjects({ data_type: dt, filter });
        } else if (data_types.length > 0) {
          const res = await api.getSetOfObjects({
            data_types: data_types,
            filter,
            filters
          });
          const dts = Object.keys(res);
          use_objects = dts.flatMap(dt => res[dt]);
        }
      }
      if (log_it) {
        // console.log(use_objects.filter(o => o._id === "5ff69ee0046efc188888f27a"));
        // console.log(use_objects.filter(o => o._id === "69c57917b2fb82125b475136"));
      }
      if (dominant_only && use_objects.length > 0 && use_objects[0] instanceof ModelBase) {
        use_objects = (use_objects as ModelBase[]).filter(o => o.is_dominant);
      }
      if (extra_filter) {
        use_objects = use_objects.filter(o => extra_filter(o));
      }
      const formatName = display_format ? (obj: UpsertableBase) => {
        return char_util.getStringWithReplacementsWithObject({ clean_me: display_format, obj });
      } : undefined;
      const detail = (obj: UpsertableBase) => {
        return obj.get_detail_format();
      }
      const toMyDisablableOption = ({
        obj, 
        formatName,
        use_field,
      }: {
        obj: string | DisablableOption | UpsertableBase, 
        formatName?: (obj: UpsertableBase) => string,
        use_field?: string,
      }) => {
        if (obj instanceof UpsertableBase && show_details) {
          return toDisablableOption({ obj, formatName, use_field, detail });
        } else {
          return toDisablableOption({ obj, formatName, use_field });
        }
      }

      const new_options: DisablableOption[] = use_objects.map(obj => toMyDisablableOption({ obj, formatName, use_field }));
      setOptions(new_options);
      const new_extra_options = extra_options.map(obj => toMyDisablableOption({ obj }));
      setExtraOptions(new_extra_options);
    }
  }, [option_objects, extra_options, data_type, data_types, filter, filters, log_it, prev_data]);

  useEffect(() => {
    setTimeout(() => {
      load();
    }, 100);
  }, [option_objects, extra_options, data_type, data_types, filter, filters, log_it, prev_data, load]);

  if (limit && options.length > limit) {
    return <span>Too many results ({ options.length }, limit is { limit })</span>
  }
  if (log_it) {
    console.log(options);
    console.log(values);
    const vs = values;
    if (vs) {
      console.log(options.filter(o => vs.includes(o.id)));
    }
  }
  return (
    <SelectStringBox
      name={name}
      value={value}
      values={values}
      options={options}
      extra_options={my_extra_options}
      color={color}
      multiple={multiple}
      disabled={disabled}
      onChange={onChange}
      auto_sort={auto_sort}
      max_selected={max_selected}
      log_it={log_it}
    />
  );
}
